import { useEffect } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import {
  Button as UIButton, IconX, Tag as UITag, WarnNote as UIWarnNote,
} from '@documents-ui';

/**
 * RUBY LIGHT-CHROME adapters over the shared @documents-ui primitives.
 *
 * RESTYLE ONLY: same props, same behaviour. @documents-ui ships dark defaults
 * because it also renders inside platform-app; these thin wrappers repaint them
 * with `!` overrides for the light super-admin pages. If @documents-ui ever
 * goes light, these become no-ops with identical class values.
 *
 * Lifted VERBATIM from the copies inside AgreementsList and PoliciesList so
 * that new legal screens do not add a third. Those two keep their own copies
 * for now — moving them is a pure-churn diff on two large files — so if you
 * change a colour here, change it there too, or move them over.
 */

const BTN_LIGHT: Record<string, string> = {
  default: '!border-stone-300 !bg-white !text-stone-700 hover:!bg-stone-50',
  primary: '!border-admin-primary !bg-admin-primary !text-white hover:!border-admin-secondary hover:!bg-admin-secondary',
  admin: '!border-admin-primary !bg-admin-primary !text-white hover:!border-admin-secondary hover:!bg-admin-secondary',
  danger: '!border-red-600 !bg-red-600 !text-white hover:!border-red-700 hover:!bg-red-700',
};

export function Button({ className = '', ...rest }: ComponentProps<typeof UIButton>) {
  return <UIButton {...rest} className={`${BTN_LIGHT[rest.variant ?? 'default']} ${className}`} />;
}

const TAG_LIGHT: Record<string, string> = {
  published: '!bg-emerald-100 !text-emerald-800',
  active: '!bg-emerald-100 !text-emerald-800',
  draft: '!bg-stone-100 !text-stone-600',
  minor: '!bg-stone-100 !text-stone-600',
  review: '!bg-amber-100 !text-amber-800',
  approved: '!bg-blue-100 !text-blue-800',
  scheduled: '!bg-rose-100 !text-rose-800',
  retired: '!bg-red-100 !text-red-800',
  material: '!bg-red-100 !text-red-800',
};

export function Tag({ className = '', ...rest }: ComponentProps<typeof UITag>) {
  return <UITag {...rest} className={`${TAG_LIGHT[rest.tone] ?? ''} ${className}`} />;
}

export function WarnNote({ className = '', ...rest }: ComponentProps<typeof UIWarnNote>) {
  return <UIWarnNote {...rest} className={`!border-amber-300 !bg-amber-50 !text-amber-800 ${className}`} />;
}

/**
 * A ONE-LINE ANSWER, WITH THE REASONING FOLDED AWAY.
 *
 * These screens carried several paragraphs of standing explanation each — how
 * acceptance is computed, what a dash means, why effective dates are UTC. The
 * content is right and hard-won, but printed in full under every table it read
 * as a wall of text nobody finishes, which costs the one sentence that actually
 * changes what an operator does.
 *
 * So the sentence that matters stays visible and the rest collapses. Kept, not
 * deleted: the day somebody asks "why does this say 0?" the answer must still
 * be here rather than in a commit message.
 *
 * `<details>` rather than state: it works before hydration, it is keyboard- and
 * screen-reader-navigable for free, and one fewer thing holds open/closed.
 */
export function Explainer({
  summary, children, className = '',
}: { summary: ReactNode; children: ReactNode; className?: string }) {
  return (
    <details
      className={`group rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm ${className}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-[12.5px] text-stone-700 marker:content-['']">
        <span className="text-stone-400 transition-transform group-open:rotate-90">›</span>
        <span className="flex-1">{summary}</span>
        <span className="shrink-0 text-[11px] text-stone-400 group-open:hidden">Details</span>
      </summary>
      <div className="mt-2.5 border-t border-stone-100 pt-2.5 text-[12px] leading-relaxed text-stone-600">
        {children}
      </div>
    </details>
  );
}

export const INPUT_CLASS =
  'w-full min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder:text-stone-400 focus:border-admin-primary focus:outline-none focus:ring-1 focus:ring-admin-primary';

/** Light modal card — same structure and behaviour as @documents-ui ModalShell. */
export function ModalShell({
  title, subtitle, children, footerNote, footerActions, onClose, labelledById,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerNote?: ReactNode;
  footerActions?: ReactNode;
  onClose: () => void;
  labelledById?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className="w-full max-w-[620px] rounded-2xl border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-stone-200 px-5 py-4">
          <div className="flex-1">
            <h3 id={labelledById} className="text-base font-semibold text-stone-900">{title}</h3>
            {subtitle && <p className="mt-1 text-[12.5px] text-stone-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <IconX size={18} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {(footerNote || footerActions) && (
          <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11.5px] text-stone-500">{footerNote}</span>
            <div className="flex gap-2">{footerActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The folder tree, flattened into dropdown options with the nesting visible.
 *
 * Sub-folders are the whole point of a move — "put this under Legal → Privacy"
 * — and a flat list of leaf names cannot express that, especially once two
 * parents both have a "Drafts". Depth is drawn with figure-space indents rather
 * than a full path so the option stays readable at a glance.
 */
export function folderOptions(
  folders: readonly { folderId: string; name: string; parentFolderId?: string }[],
): { value: string; label: string }[] {
  const children = new Map<string, typeof folders[number][]>();
  for (const f of folders) {
    const key = f.parentFolderId ?? '';
    children.set(key, [...(children.get(key) ?? []), f]);
  }
  // A folder whose parent is missing (deleted, or not readable) would otherwise
  // vanish from the picker along with everything under it.
  const known = new Set(folders.map((f) => f.folderId));
  const orphans = folders.filter((f) => f.parentFolderId && !known.has(f.parentFolderId));

  const out: { value: string; label: string }[] = [];
  const walk = (parentId: string, depth: number, seen: Set<string>) => {
    for (const f of (children.get(parentId) ?? []).sort((a, b) => a.name.localeCompare(b.name))) {
      // A parent chain that loops back on itself must not hang the picker.
      if (seen.has(f.folderId)) continue;
      out.push({ value: f.folderId, label: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${f.name}` });
      walk(f.folderId, depth + 1, new Set([...seen, f.folderId]));
    }
  };
  walk('', 0, new Set());
  for (const o of orphans) out.push({ value: o.folderId, label: o.name });
  return out;
}
